'use client';

import { DateTimeField as SharedDateTimeField } from '@kit/ui/date-time-field';

/**
 * Auditions date+time field. Thin adapter over the shared
 * `@kit/ui/date-time-field` so the whole app has ONE date/time UI (this used to
 * be a bespoke Calendar+Popover copy). The auditions forms store these fields
 * as `Date | null`, so this adapter converts to/from the shared component's
 * `YYYY-MM-DDTHH:mm` string at the boundary — WITHOUT crossing UTC (Critical
 * Rule 19: no `toISOString()` / `new Date('YYYY-MM-DD')`).
 */
function dateToLocalInput(value: Date | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${day}T${h}:${mi}`;
}

function localInputToDate(value: string): Date | null {
  if (!value) return null;
  const [datePart = '', timePart = '00:00'] = value.split('T');
  const [y, mo, day] = datePart.split('-').map(Number);
  const [h, mi] = timePart.split(':').map(Number);
  if (!y || !mo || !day) return null;
  return new Date(y, mo - 1, day, h || 0, mi || 0, 0, 0);
}

export function DateTimeField({
  value,
  onChange,
  placeholder,
  timeLabel,
  disabled,
}: {
  value: Date | null | undefined;
  onChange: (value: Date | null) => void;
  /** Shown on the trigger when no value is set. */
  placeholder: string;
  /** Accessible label for the time input. */
  timeLabel: string;
  disabled?: boolean;
}) {
  return (
    <SharedDateTimeField
      value={dateToLocalInput(value)}
      onChange={(next) => onChange(localInputToDate(next))}
      placeholder={placeholder}
      timeAriaLabel={timeLabel}
      disabled={disabled}
    />
  );
}
